import { Typography, useTheme } from '@mui/material';
import { VideoType } from '@tmdb-graphql-api/schema/types';
import { Metadata } from '#components/Metadata/index.tsx';
import { MoreOptionButton } from '#components/MoreOptionsButton/index.tsx';
import { useIsAncestor } from '#contexts/Ancestors/index.ts';
import { Ancestor } from '#enums/ancestors.ts';
import { mapSectionTypeToScreenType } from '#helpers/enumMappers.ts';
import { isMovieOrTv } from '#helpers/isMovieOrTv.ts';
import { tt } from '#i18n/index.ts';
import { PosterImage } from '../PosterImage/index.tsx';
import { TextLink } from '../TextLink/index.tsx';
import { Small as VoteAverageBadge } from '../VoteAverageBadge/index.tsx';
import { Container, Content, PrimaryContent, SupportingContent } from './styled.ts';
import { type ImageProps, type TileProps } from './types.ts';

export const Tile = ({
  id,
  lastItemRef,
  metadata,
  orientation,
  renderAdditionalOptionItems,
  renderImage,
  setActivePreview,
  setActiveVideo,
  showMoreOptions = false,
  src,
  status,
  subTitle,
  sx,
  tagline,
  title,
  type,
  videoPathIds,
  voteAverage,
}: TileProps) => {
  const theme = useTheme();
  const isListingPreviewAncestor = useIsAncestor(Ancestor.ListingPreview);
  const isHorizontal = orientation === 'horizontal';

  const imageProps: ImageProps = {
    id,
    pathIds: videoPathIds,
    setActivePreview,
    setActiveVideo,
    showCertification: true,
    showMoreOptions: true,
    src,
    status,
    title,
    type,
    videoTypes: [VideoType.Trailer, VideoType.Clip],
  };

  return (
    <Container
      ref={lastItemRef}
      sx={{
        flexDirection: isHorizontal ? 'row' : undefined,
        position: 'relative',
        width: isHorizontal ? '100%' : undefined,
        ...sx,
      }}
      tabIndex={0}
    >
      {renderImage ? renderImage(imageProps) : <PosterImage {...imageProps} />}
      <Content
        sx={{
          justifyContent: isHorizontal ? 'space-between' : undefined,
          marginLeft: isHorizontal ? theme.spacing(2) : undefined,
        }}
      >
        <PrimaryContent sx={showMoreOptions ? { paddingRight: '35px' } : undefined}>
          <Typography
            className="listItemTitle"
            sx={{ color: theme.mainTextPrimary, fontWeight: isHorizontal ? 'bold' : undefined }}
            variant="body2"
          >
            <TextLink
              onClick={e => {
                if (!isListingPreviewAncestor && setActivePreview) {
                  e.preventDefault();
                  setActivePreview({ id, type });
                }
              }}
              sx={{
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 3,
                color: theme.mainTextPrimary,
                cursor: 'pointer',
                display: '-webkit-box',
                overflow: 'hidden',
              }}
              to={`/details/${type.toLowerCase()}/${String(id)}`}
            >
              {title}
            </TextLink>
          </Typography>
          {isHorizontal && tagline ? (
            <Typography
              className="tagline"
              sx={{ color: theme.mainTextPrimary, marginTop: theme.spacing(0.5) }}
              variant="body2"
            >
              {tagline}
            </Typography>
          ) : undefined}
          {isHorizontal && metadata?.length ? (
            <Metadata metadata={metadata} sx={{ marginTop: theme.spacing(0.5) }} />
          ) : undefined}
        </PrimaryContent>
        <SupportingContent>
          <Typography
            className="listItemSubTitle"
            sx={{
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 3,
              color: theme.supportingTextPrimary,
              display: '-webkit-box',
              overflow: 'hidden',
            }}
            variant="body2"
          >
            {subTitle ?? tt(`screenTypes.${mapSectionTypeToScreenType(type)}`)}
          </Typography>
          {voteAverage ? <VoteAverageBadge voteAverage={voteAverage} /> : undefined}
        </SupportingContent>
      </Content>
      {isMovieOrTv(type) && showMoreOptions && status ? (
        <MoreOptionButton
          // @ts-expect-error move and tv ids are always numbers
          id={id}
          renderAdditionalOptionItems={renderAdditionalOptionItems}
          status={status}
          sx={{ position: 'absolute', right: '-4px', top: '-4px' }}
          type={mapSectionTypeToScreenType(type)}
        />
      ) : undefined}
    </Container>
  );
};
